import React, { useState, useEffect } from 'react';
import { OnboardingStepProps } from '../../../types/onboarding';
import { StepTitle, StepDescription, StepContent } from '../../../styles/onboarding/Onboarding.styles';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

const PreferencesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const PreferenceGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const RangeInput = styled.input`
  flex: 1;
  height: 8px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.background.secondary};
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.primary};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.primary};
    cursor: pointer;
    border: none;
  }
`;

const RangeValue = styled.span`
  min-width: 60px;
  text-align: center;
  font-weight: 600;
  color: ${theme.colors.primary};
`;

const GenderOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const GenderOption = styled.button<{ $isSelected: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${props => props.$isSelected ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$isSelected ? theme.colors.primary : 'transparent'};
  color: ${props => props.$isSelected ? 'white' : theme.colors.text.primary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

export const PreferencesStep: React.FC<OnboardingStepProps> = ({
  data,
  onChange,
  onSkip
}) => {
  const [preferences, setPreferences] = useState({
    ageRange: { min: 18, max: 99 },
    maxDistance: 50,
    showMe: [] as string[],
    // Relationship intent. Stored on the user row (users.looking_for), and
    // weighted double in the match algorithm's lifestyle component — nothing
    // in onboarding collected it before, so it could never be filled in.
    lookingFor: '' as string
  });

  useEffect(() => {
    if (data?.preferences) {
      setPreferences(prev => ({ ...prev, ...data.preferences }));
    }
  }, []);

  // Report validity honestly. This passed a hardcoded `true`, so the step
  // announced itself complete on mount with nothing chosen — Next was live
  // from the first frame, and clicking straight through saved an empty
  // gender preference. Combined with canSkip, the Next button could never be
  // disabled here at all, which is how accounts reached the end of onboarding
  // with no preferences on record.
  //
  // Gender is the field that actually gates who you are shown, so it is the
  // one required answer. Age and distance keep working defaults.
  const hasChosenGenders = preferences.showMe.length > 0;

  useEffect(() => {
    onChange({ preferences }, hasChosenGenders);
  }, [preferences, hasChosenGenders]);

  const genderOptions = ['male', 'female', 'non-binary', 'other'];

  // Must match the users.looking_for CHECK constraint (migration 013).
  const lookingForOptions: Array<{ value: string; label: string }> = [
    { value: 'long-term', label: 'Long-term' },
    { value: 'serious', label: 'Something serious' },
    { value: 'dating', label: 'Dating' },
    { value: 'casual', label: 'Casual' },
    { value: 'friendship', label: 'Friendship' },
  ];

  const toggleGender = (gender: string) => {
    setPreferences(prev => ({
      ...prev,
      showMe: prev.showMe.includes(gender)
        ? prev.showMe.filter(g => g !== gender)
        : [...prev.showMe, gender]
    }));
  };

  return (
    <>
      <StepTitle>Set Your Preferences</StepTitle>
      <StepDescription>
        Customize your dating preferences to find the perfect match.
      </StepDescription>
      <StepContent>
        <PreferencesContainer>
          <PreferenceGroup>
            <Label>Age Range</Label>
            <RangeContainer>
              <RangeInput
                type="range"
                min="18"
                max="99"
                value={preferences.ageRange.min}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  ageRange: { ...prev.ageRange, min: parseInt(e.target.value) }
                }))}
              />
              <RangeValue>{preferences.ageRange.min} - {preferences.ageRange.max}</RangeValue>
              <RangeInput
                type="range"
                min="18"
                max="99"
                value={preferences.ageRange.max}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  ageRange: { ...prev.ageRange, max: parseInt(e.target.value) }
                }))}
              />
            </RangeContainer>
          </PreferenceGroup>

          <PreferenceGroup>
            <Label>Maximum Distance: {preferences.maxDistance} miles</Label>
            <RangeInput
              type="range"
              min="1"
              max="100"
              value={preferences.maxDistance}
              onChange={(e) => setPreferences(prev => ({
                ...prev,
                maxDistance: parseInt(e.target.value)
              }))}
            />
          </PreferenceGroup>

          <PreferenceGroup>
            {/* Say it is required, rather than letting the disabled Next
                button be the only clue. */}
            <Label>Show Me{hasChosenGenders ? '' : ' — pick at least one to continue'}</Label>
            <GenderOptions>
              {genderOptions.map(gender => (
                <GenderOption
                  key={gender}
                  $isSelected={preferences.showMe.includes(gender)}
                  onClick={() => toggleGender(gender)}
                >
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </GenderOption>
              ))}
            </GenderOptions>
          </PreferenceGroup>

          <PreferenceGroup>
            <Label>What are you looking for?</Label>
            <GenderOptions>
              {lookingForOptions.map(option => (
                <GenderOption
                  key={option.value}
                  $isSelected={preferences.lookingFor === option.value}
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    lookingFor: prev.lookingFor === option.value ? '' : option.value,
                  }))}
                >
                  {option.label}
                </GenderOption>
              ))}
            </GenderOptions>
          </PreferenceGroup>
        </PreferencesContainer>
      </StepContent>
    </>
  );
};

